import React from 'react';
import { useIntl } from 'react-intl';
import Select from 'react-select';

import PropTypes from 'prop-types';

import ClientDetails from '@/components/CommercialPage/ClientDetails';
import Card from '@/components/common/Card';
import ColorButton from '@/components/common/elements/ColorButton';
import Spinner from '@/components/common/Spinner';
import Checkbox from '@/components/common/elements/Checkbox';
import inspectionTypesIcon from '@/components/common/icons/records.svg';
import userPermissionImage from '@/components/common/icons/user-permission.svg';

const SectionTitle = ({ icon, children }) => (
  <h5 className="flex items-center mt-[1.5em] mb-2 text-[0.93875em] !font-bold leading-[1.1] text-[#393939]">
    <div className="w-[24px] h-[24px] mr-[0.5em] flex items-center justify-center flex-shrink-0">
      <img src={icon} alt="" className="h-[20px] w-auto" />
    </div>
    {children}
  </h5>
);

SectionTitle.propTypes = {
  icon: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const ClientAssignFormComponent = ({
  busy,
  requestInProgress,
  client,
  franchisees,
  handleChangeFranchisee,
  handleChangeMailable,
  onSubmit,
  intl,
}) => {
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '34px',
      borderRadius: '3px',
      borderColor: state.isFocused ? '#4d8fca' : '#e3e3e3',
      boxShadow: 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#4d8fca' : '#e3e3e3',
      },
    }),
    valueContainer: (base) => ({
      ...base,
      maxWidth: 'calc(100% - 73px)',
      padding: '0 8px',
      '& [class$="Input"]': {
        padding: 0,
        lineHeight: 0,
      },
      '& .select__input': {
        padding: '4px 0',
        height: '24px',
        '& input': {
          height: '100% !important',
          background: 'transparent !important',
          border: 'none !important',
          boxShadow: 'none !important',
          padding: '0 !important',
          margin: '0 !important',
        },
      },
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: '12px',
      color: '#b1b1b1',
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: '12px',
      color: '#919191',
      fontWeight: '600',
    }),
    input: (base) => ({
      ...base,
      fontSize: '12px',
    }),
    menu: (base) => ({
      ...base,
      fontSize: '12px',
      '& .select__menu-list': {
        maxHeight: '148px',
        height: '148px',
        '& .select__option': {
          fontSize: '12px',
          cursor: 'pointer',
        },
      },
    }),
    multiValue: (base) => ({
      ...base,
      borderRadius: '15px',
      backgroundColor: '#658ec6',
      color: '#ffffff',
    }),
    multiValueLabel: (base) => ({
      ...base,
      padding: '2px 2px 2px 8px',
      fontSize: '12px',
      color: '#ffffff',
    }),
    multiValueRemove: (base) => ({
      ...base,
      borderRadius: '0 13px 13px 0',
      padding: '2px 5px 2px 2px',
      backgroundColor: '#658ec6',
      cursor: 'pointer',
      transition: 'background-color 0.3s',
      '&:hover': {
        backgroundColor: '#5079b3',
        color: '#ffffff',
      },
    }),
  };

  if (busy) {
    return (
      <div className="flex flex-col p-[1.5em_1em_0]">
        <div className="flex justify-center mt-5">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <>
      <ClientDetails busy={busy} client={client} intl={intl} />
      <div className="flex flex-col p-[1.5em_1em]">
        <Card className="fade-in !p-[1.75em_1.625em]">
          <h4 className="flex items-center mb-[1.4em] pb-[1.4em] border-b border-[#eee] text-[1.25em] font-bold leading-[1.3] text-[#393939]">
            <div className="mr-[0.5em] w-[28px] h-[28px] flex items-center justify-center">
              <img src={userPermissionImage} alt={intl.formatMessage({ id: 'ASSIGN_CUSTOMER_MESSAGE' })} className="max-h-full max-w-full" />
            </div>
            {intl.formatMessage({ id: 'ASSIGN_CUSTOMER_MESSAGE' })}
          </h4>
          <form onSubmit={onSubmit}>
            <SectionTitle icon={inspectionTypesIcon}>
              {intl.formatMessage({ id: 'FRANCHISES' })}
            </SectionTitle>
            <div className="flex flex-col mt-[1.25em] px-[3.46em]">
              <Select
                id="commercialFranchiseesSelect"
                name="franchisee"
                placeholder={intl.formatMessage({
                  id: 'SELECT_ONE_FRANCHISE_MESSAGE',
                })}
                classNamePrefix="select"
                styles={selectStyles}
                isClearable
                required
                options={franchisees}
                noOptionsMessage={() => intl.formatMessage({ id: 'NO_FRANCHISES_AVAILABLE_MESSAGE' })}
                isDisabled={!franchisees.length}
                onChange={handleChangeFranchisee}
              />
            </div>
            <div className="flex items-center mt-[1.25em] px-[3.46em] opacity-50">
              <Checkbox
                id="mailable"
                type="checkbox"
                className="mr-[10px]"
                color="primary"
                onChange={handleChangeMailable}
                disabled
              />
              <label htmlFor="mailable" className="m-0 cursor-not-allowed text-[0.93875em] font-bold text-[#393939] uppercase">
                {intl.formatMessage({ id: 'SEND_EMAIL_WITH_USER_MESSAGE' })}
              </label>
            </div>
            <div className="flex justify-center items-center mt-[1.75em] pt-[1.75em] border-t border-[#eee]">
              <ColorButton type="submit" color="green" isBusy={requestInProgress}>
                {intl.formatMessage({ id: 'SAVE' })}
              </ColorButton>
            </div>
          </form>
        </Card>
      </div>
    </>
  );
};

ClientAssignFormComponent.propTypes = {
  busy: PropTypes.bool.isRequired,
  requestInProgress: PropTypes.bool.isRequired,
  client: PropTypes.object,
  franchisees: PropTypes.array,
  handleChangeFranchisee: PropTypes.func.isRequired,
  handleChangeMailable: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
};

ClientAssignFormComponent.defaultProps = {
  client: null,
  franchisees: [],
};

export default ClientAssignFormComponent;
